<template>
  <div class="review-action-link" :class="{ 'review-action-link_editing': editing }">
    <span v-if="label" class="review-action-link__label">{{ label }}</span>

    <template v-if="editing">
      <div class="review-action-link__editor">
        <v-btn-toggle
          v-model="draftMode"
          mandatory
          dense
          class="review-action-link__modes"
        >
          <v-btn small value="default">
            По умолчанию
          </v-btn>
          <v-btn small value="none">
            Не показывать
          </v-btn>
          <v-btn small value="custom">
            Своя ссылка
          </v-btn>
        </v-btn-toggle>

        <div class="review-action-link__editor-row">
          <v-text-field
            v-if="draftMode === 'custom'"
            ref="input"
            v-model="draft"
            class="review-action-link__input"
            placeholder="https://…"
            outlined
            dense
            hide-details="auto"
            :error-messages="error"
            @keydown.enter.prevent="done"
            @keydown.esc.prevent="cancel"
          />
          <span v-else class="review-action-link__editor-hint">
            {{ editorHint }}
          </span>
          <v-btn
            small
            depressed
            color="primary"
            @click="done"
          >
            Готово
          </v-btn>
          <v-btn small text @click="cancel">
            Отмена
          </v-btn>
        </div>
      </div>
    </template>

    <template v-else>
      <a
        v-if="effective"
        class="review-action-link__url"
        :href="effective"
        :title="effective"
        target="_blank"
        rel="noopener"
      >{{ shorten(effective) }}</a>
      <span
        v-else-if="mode === 'none'"
        class="review-action-link__empty review-action-link__empty_muted"
      >Не показывать</span>
      <span v-else class="review-action-link__empty" :class="{ 'review-action-link__empty_muted': !highlightEmpty }">
        Ссылка не задана
      </span>

      <span
        v-if="editable && mode === 'default' && effective"
        class="review-action-link__tag"
        title="Ссылка по умолчанию из конфига сети, у филиала своей нет"
      >по умолчанию</span>
      <span
        v-if="editable && mode === 'none'"
        class="review-action-link__tag review-action-link__tag_off"
        title="Филиал отключил ссылку, сетевая по умолчанию не используется"
      >отключена</span>

      <v-btn
        v-if="editable"
        icon
        small
        :disabled="disabled"
        title="Изменить"
        @click="edit"
      >
        <v-icon small>
          mdi-pencil
        </v-icon>
      </v-btn>
      <v-icon
        v-else
        small
        class="review-action-link__lock"
        title="Задаётся для всей сети"
      >
        mdi-lock-outline
      </v-icon>
    </template>
  </div>
</template>

<script>
  /**
   * Три состояния своей ссылки филиала (prop value):
   *   null - по умолчанию (используется сетевая defaultValue),
   *   ''   - отключена (клиенту не показывается),
   *   URL  - своя.
   */
  export default {
    name: 'ReviewActionLink',
    props: {
      value: { type: String, default: null },
      defaultValue: { type: String, default: null },
      label: { type: String, default: null },
      editable: { type: Boolean, default: true },
      disabled: Boolean,
      // пустая ссылка красным (сервис включён) или серым (сервис выключен)
      highlightEmpty: { type: Boolean, default: true },
    },
    data () {
      return {
        editing: false,
        draftMode: 'default',
        draft: '',
        error: null,
      }
    },
    computed: {
      mode () {
        if (this.value === null || this.value === undefined) return 'default'
        return this.value === '' ? 'none' : 'custom'
      },
      effective () {
        if (this.mode === 'custom') return this.value
        if (this.mode === 'default') return this.defaultValue || null
        return null
      },
      editorHint () {
        if (this.draftMode === 'none') return 'Сервис не будет предлагаться клиенту'
        if (this.defaultValue) return `По умолчанию: ${this.shorten(this.defaultValue)}`
        return 'Ссылки по умолчанию пока нет - сервис появится у клиента, как только её заполнят в сети'
      },
    },
    methods: {
      shorten (url) {
        return (url || '').replace(/^https?:\/\//, '')
      },
      edit () {
        this.draftMode = this.mode
        this.draft = this.mode === 'custom' ? this.value : ''
        this.error = null
        this.editing = true
        this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
      },
      cancel () {
        this.editing = false
        this.error = null
      },
      done () {
        if (this.draftMode === 'custom') {
          const next = this.draft.trim()
          if (!/^https?:\/\/\S+$/.test(next)) {
            this.error = 'Ссылка должна начинаться с http:// или https://'
            return
          }
          this.emitAndClose(next)
          return
        }
        this.emitAndClose(this.draftMode === 'none' ? '' : null)
      },
      emitAndClose (next) {
        this.editing = false
        this.error = null
        this.$emit('input', next)
      },
    },
  }
</script>

<style scoped lang="less">
.review-action-link {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  min-width: 0;

  &_editing { align-items: flex-start; }

  &__label {
    flex: 0 0 78px;
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #9a9ea6;
  }

  &_editing &__label { margin-top: 8px; }

  &__url {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: #1d1d1f;
    text-decoration: none;
    border-bottom: 1px solid #d6d6d7;

    &:hover { border-color: @color-primary; }
  }

  &__empty {
    font-size: 13px;
    color: @color-error;

    &_muted { color: #9a9ea6; }
  }

  &__editor {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }

  // Поле dense outlined у Vuetify высотой 40px; кнопки такой же высоты и прижаты к верху,
  // чтобы текст ошибки под полем не сдвигал их вниз
  @editor-height: 40px;

  &__editor-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    .v-btn.v-btn { height: @editor-height; }
  }

  &__editor-hint {
    flex: 1;
    min-width: 0;
    min-height: @editor-height;
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #6d6d6d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__modes.v-btn-toggle {
    border-radius: 5px;

    .v-btn {
      text-transform: none;
      letter-spacing: normal;
    }

    .v-btn:first-child { border-radius: 5px 0 0 5px; }
    .v-btn:last-child { border-radius: 0 5px 5px 0; }

    // активный режим - как кнопка «Сохранить»: заливка primary, белый текст
    .v-btn.v-btn--active {
      background-color: @color-primary !important;
      border-color: @color-primary;
      color: #fff;

      &::before { opacity: 0; }
    }
  }

  &__input { flex: 1; min-width: 0; }

  &__tag {
    flex: none;
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6d6d6d;
    background: #f1f3f6;

    &_off {
      color: #9a9ea6;
      background: transparent;
      border: 1px dashed #d6d6d7;
    }
  }

  &__lock { color: #9a9ea6; }
}
</style>
